import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { Form, Input } from 'semantic-ui-react';
import isNumeric from 'validator/es/lib/isNumeric';
import {
  clientError,
  firstError,
  firstResponseError,
  pruneServerErrors,
  invalidUrlCharacters, validationClasses,
} from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import fields, { globalFieldLengths } from '@/constants/fields';
import urlDenyCharacters from '@/constants/validation';
import FormValidationError from '@/components/errors/FormValidationError';
import Dialog from '@/components/dialogs/Dialog';
import VisibilityDropdown from '@/components/VisibilityDropdown';
import { entityCommonsVisibility } from '@/constants/states';
import WorkflowsDropdown from '@/workflows/components/WorkflowsDropdown';
import { createActiveDataset, hideSaveDataset, updateActiveDatasetProperties } from '../actions';

const SaveDatasetDialog = ({
  allowWorkflowList,
  code,
  commonsMode,
  hide,
  name,
  postData,
  saveErrors,
  saveStatus,
  theme,
  updateProperties,
  visibility,
  workflowKey,
}) => {
  const intl = useIntl();
  const updateSettings = (properties) => (updateProperties({ settings: properties }));

  const setErrors = (newErrors) => (updateProperties({ saveErrors: newErrors }));

  const setStatus = (status) => (updateProperties({ saveStatus: status }));

  const validateForm = () => {
    const validationErrors = [];

    if (code && (isNumeric(code) || invalidUrlCharacters(code))) {
      validationErrors.push(
        clientError(
          { entity: 'dataset' },
          intl.formatMessage({ id: 'save.dataset.validation.code.title' }),
          intl.formatMessage({ id: 'validation.invalid.code' }, { characters: urlDenyCharacters }),
          [fields.CODE],
        ),
      );
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);

    return validationErrors;
  };

  const submitForm = () => {
    setStatus(states.PENDING);
    const validationErrors = checkForErrors();

    if (validationErrors.length > 0) {
      setStatus(states.NONE);
      return;
    }

    postData();
  };

  const codeError = saveErrors ? saveErrors.find((e) => (e.fields.includes(fields.CODE))) : null;

  return (
    <Dialog
      className="aridhia-save-dataset-dialog"
      icon="table"
      inProgress={saveStatus === states.PENDING}
      message="dataset.create.text"
      messageError={firstResponseError(saveErrors)}
      messageIcon="save"
      minimumDataEntryMet={name}
      onClose={hide}
      onDismiss={() => setErrors(pruneServerErrors(saveErrors))}
      onSave={submitForm}
      saveMessageId="save.dataset.create.button"
      theme={theme}
      title={intl.formatMessage({ id: 'save.dataset.create.title' })}
      values={{ name }}
    >
      <Form autoComplete="off" onSubmit={submitForm} error={firstError(saveErrors) !== null}>
        <FormValidationError error={codeError} htmlFor="dataset_code" labelId="save.dataset.code">
          <Input
            id="dataset_code"
            size="large"
            disabled={saveStatus === states.PENDING}
            type="custom"
            placeholder={intl.formatMessage({ id: 'save.dataset.code.placeholder' })}
            value={code || ''}
            maxLength={globalFieldLengths.code}
            onChange={(e) => updateProperties({ steps: { code: e.target.value } })}
            className={validationClasses(codeError)}
          />
        </FormValidationError>
        <FormValidationError htmlFor="dataset_visibility" labelId="dataset.settings.visibility.field.label">
          <VisibilityDropdown
            commonsMode={commonsMode}
            disabled={saveStatus === states.PENDING}
            id="dataset_visibility"
            onChange={updateSettings}
            visibility={visibility}
            {...commonsMode && { visibilityOptions: Object.values(entityCommonsVisibility) }}
          />
        </FormValidationError>
        {allowWorkflowList && (
          <FormValidationError htmlFor="dataset_workflow" labelId="save.dataset.workflow">
            <WorkflowsDropdown
              category="lookups"
              disabled={saveStatus === states.PENDING}
              onChange={(workflow) => updateSettings({ workflow_key: workflow ? workflow.code : null })}
              workflowKey={workflowKey}
            />
          </FormValidationError>
        )}
      </Form>
    </Dialog>
  );
};

const mapStateToProps = ({
  datasets: {
    activeDataset: {
      saveErrors, saveStatus, steps, settings, catalogue: { title },
    },
  },
  auth: {
    role: { permissions: { workflows: { read } } },
    commonsMode,
  },
}) => ({
  allowWorkflowList: read,
  code: steps?.code,
  commonsMode,
  name: title,
  visibility: settings.visibility,
  saveErrors,
  saveStatus,
  workflowKey: settings.workflow_key,
});

const mapDispatchToProps = {
  hide: hideSaveDataset,
  updateProperties: updateActiveDatasetProperties,
  postData: createActiveDataset,
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveDatasetDialog);
